import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLImageElement} [productImage] - Product image element (desktop only)
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view, and mirrors the inline
 * Single/Pair form: clicking a sticky button forwards to the matching inline
 * button so the real product form sets the quantity and submits.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  #resetTimeouts = new Map();

  /** @type {boolean} */
  #isStuck = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    this.addEventListener('click', this.#handleClick, { signal });

    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    for (const timeoutId of this.#resetTimeouts.values()) clearTimeout(timeoutId);
    this.#resetTimeouts.clear();
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const rect = buyButtonsBlock.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            if (!this.#isChatActive()) this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
  }

  /**
   * Routes clicks on the sticky buttons to the inline product form.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const notifyButton = target.closest('[data-notify-me]');
    if (notifyButton) {
      notifyButton.dispatchEvent(
        new CustomEvent('pact:back-in-stock:show', {
          detail: {
            id: notifyButton.getAttribute('data-variant-id'),
            handle: notifyButton.getAttribute('data-product-handle'),
          },
          bubbles: true,
        })
      );
      return;
    }

    const stickyButton = target.closest('[data-sticky-atc]');
    if (!(stickyButton instanceof HTMLElement)) return;

    const quantity = stickyButton.getAttribute('data-quantity') || '1';
    const inlineButton = this.#getInlineButton(quantity);
    if (!inlineButton) {
      console.warn('[sticky-add-to-cart] Could not find the inline add to cart button.');
      return;
    }

    // Suppress the inline fly-to-cart so only the sticky bar animates.
    inlineButton.dataset.puppet = 'true';

    const animationPromise = this.#animateAdded(stickyButton);
    inlineButton.stickyAddToCartAnimation = animationPromise;
    try {
      inlineButton.click();
    } finally {
      inlineButton.dataset.puppet = 'false';
    }

    const clearAnimationPromise = () => {
      if (inlineButton.stickyAddToCartAnimation === animationPromise) {
        delete inlineButton.stickyAddToCartAnimation;
      }
    };
    animationPromise.then(clearAnimationPromise, clearAnimationPromise);
  };

  /**
   * Finds the inline add to cart button that matches a quantity.
   * @param {string} quantity
   * @returns {HTMLButtonElement | null}
   */
  #getInlineButton(quantity) {
    const productForm = this.#getProductForm();
    const inlineButtons = productForm?.querySelector('single-pair-atc-buttons');
    return inlineButtons?.querySelector(`button[data-quantity="${quantity}"]`) ?? null;
  }

  /**
   * Plays the added state and the fly-to-cart animation from the sticky bar.
   * @param {HTMLElement} button - The clicked sticky button
   */
  async #animateAdded(button) {
    if (button.dataset.added !== 'true') button.dataset.added = 'true';

    const { productImage } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    /** @type {FlyToCart | null} */
    let flyToCartElement = null;

    // productImage only exists on desktop; skip the flight when it is hidden.
    if (cartIcon && productImage && productImage.offsetParent !== null) {
      flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
      flyToCartElement.classList.add('fly-to-cart--sticky');
      flyToCartElement.style.setProperty('background-image', `url(${productImage.src})`);
      flyToCartElement.useSourceSize = 'true';
      flyToCartElement.source = productImage;
      flyToCartElement.destination = cartIcon;
      document.body.appendChild(flyToCartElement);
    }

    const animated = flyToCartElement ? [button, flyToCartElement] : [button];
    await onAnimationEnd(animated);

    const currentTimeout = this.#resetTimeouts.get(button);
    if (currentTimeout !== undefined) clearTimeout(currentTimeout);

    const timeoutId = setTimeout(() => {
      button.removeAttribute('data-added');
      if (this.#resetTimeouts.get(button) === timeoutId) {
        this.#resetTimeouts.delete(button);
      }
    }, 800);
    this.#resetTimeouts.set(button, timeoutId);
  }

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const { optionValueId } = event.detail ?? {};
    if (optionValueId) this.dataset.currentVariantId = optionValueId;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;
        if (productId && productId !== this.dataset.productId) return;

        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';

        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);

        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        if (variant == null) this.#handleVariantUnavailable();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant label based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '');
    if (selectedOptions.length === 0) return;

    const variant = this.querySelector('.sticky-add-to-cart__variant');
    if (variant) variant.textContent = selectedOptions.join(' / ');
  };

  /**
   * Shows the sticky bar
   */
  #showStickyBar() {
    this.#isStuck = true;
    this.refs.stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar
   */
  #hideStickyBar() {
    this.#isStuck = false;
    this.refs.stickyBar.dataset.stuck = 'false';
  }

  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
